/**
 * The form builder shell.
 *
 * The field editor, the sortable cards, the group frame and the preview are
 * ported from the-answer-2 (BACKLOG §16). This shell is NOT: theirs was wired
 * to their drafts store, router state, feature-announcement dialog and toast
 * host, and keyed grouping off a numeric group id. Per the porting rule — the
 * shell is where the coupling lives, so the shell gets rewritten — this is
 * savvy's, and it is fully controlled: it owns no server state and performs no
 * fetches. The host passes fields down and receives every change back.
 */

import { useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Eye, LayoutGrid, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

import { FormFieldEditor } from "./form-field-editor";
import { FormPreview } from "./form-preview";
import { SortableFormElement } from "./sortable-form-field";
import { SortableGroup } from "./sortable-group";
import {
  FIELD_PRESETS,
  FIELD_PRESET_GROUPS,
  makeField,
  makeFieldGroup,
  type FieldPreset,
} from "./form-elements";
import {
  MAX_FORM_FIELDS,
  deriveFieldKey,
  groupFields,
  uniqueFieldKey,
  type FormField,
} from "./types";

export interface FormBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
  /** Shown above the preview so the operator sees it as the agent will. */
  formTitle?: string;
  /** Surfaced by the host — a save error, a validation message. */
  notice?: string | null;
  disabled?: boolean;
}

export function FormBuilder({
  fields,
  onChange,
  formTitle = "Preview",
  notice,
  disabled = false,
}: FormBuilderProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const takenKeys = useMemo(() => fields.map((f) => f.key), [fields]);
  const editing = fields.find((f) => f.id === editingId) ?? null;
  const dragging = fields.find((f) => f.id === draggingId) ?? null;
  const full = fields.length >= MAX_FORM_FIELDS;

  function addPreset(preset: FieldPreset) {
    if (full) return;
    const field = makeField(preset, takenKeys);
    onChange([...fields, field]);
    setEditingId(field.id);
  }

  function addPresetGroup(group: (typeof FIELD_PRESET_GROUPS)[number]) {
    const batch = makeFieldGroup(group, takenKeys);
    if (fields.length + batch.length > MAX_FORM_FIELDS) return;
    onChange([...fields, ...batch]);
  }

  function updateField(next: FormField) {
    // A renamed label does NOT rename the key: the key is a contract with
    // delivery templates, so it only changes when the operator changes it.
    onChange(fields.map((f) => (f.id === next.id ? next : f)));
  }

  function changeType(id: string, type: FormField["type"]) {
    onChange(
      fields.map((f) => {
        if (f.id !== id) return f;
        const next: FormField = { ...f, type };
        // Options belong to a radio group and nothing else — carrying them
        // across a type change is what the server rejects.
        if (type === "radio-group") next.options = f.options?.length ? f.options : ["Yes", "No"];
        else delete next.options;
        if (type !== "link") delete next.url;
        return next;
      }),
    );
  }

  function removeField(id: string) {
    onChange(fields.filter((f) => f.id !== id));
    if (editingId === id) setEditingId(null);
  }

  function duplicateField(id: string) {
    const source = fields.find((f) => f.id === id);
    if (!source || full) return;
    const copy: FormField = {
      ...source,
      id: crypto.randomUUID(),
      key: uniqueFieldKey(deriveFieldKey(source.label), takenKeys),
    };
    const at = fields.findIndex((f) => f.id === id);
    onChange([...fields.slice(0, at + 1), copy, ...fields.slice(at + 1)]);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDraggingId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = fields.findIndex((f) => f.id === active.id);
    const to = fields.findIndex((f) => f.id === over.id);
    if (from < 0 || to < 0) return;

    // Moving a field onto another adopts that field's section, which is how a
    // field joins or leaves a group without a separate gesture.
    const moved = arrayMove(fields, from, to);
    const landedOn = fields[to];
    onChange(
      moved.map((f) =>
        f.id === active.id ? { ...f, group: landedOn?.group } : f,
      ),
    );
  }

  function handleDragStart(event: DragStartEvent) {
    setDraggingId(String(event.active.id));
  }

  const sections = groupFields(fields);

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <aside className="lg:w-64 lg:shrink-0">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Add a field</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              {FIELD_PRESETS.map((preset) => (
                <button
                  key={preset.type + preset.label}
                  type="button"
                  disabled={disabled || full}
                  onClick={() => addPreset(preset)}
                  className="hover:bg-accent focus-visible:ring-ring flex flex-col rounded-md px-2 py-1.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
                >
                  <span className="text-sm font-medium">{preset.label}</span>
                  <span className="text-muted-foreground text-xs">{preset.hint}</span>
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-1 border-t pt-3">
              <span className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">
                Sections
              </span>
              {FIELD_PRESET_GROUPS.map((group) => (
                <button
                  key={group.label}
                  type="button"
                  disabled={disabled}
                  onClick={() => addPresetGroup(group)}
                  className="hover:bg-accent focus-visible:ring-ring flex flex-col rounded-md px-2 py-1.5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none disabled:opacity-50"
                >
                  <span className="flex items-center gap-1.5 text-sm font-medium">
                    <Plus className="h-3 w-3" />
                    {group.label}
                  </span>
                  <span className="text-muted-foreground text-xs">{group.hint}</span>
                </button>
              ))}
            </div>

            {full && (
              <p className="text-destructive text-xs">
                This form is at the {MAX_FORM_FIELDS}-field limit.
              </p>
            )}
          </CardContent>
        </Card>
      </aside>

      <div className="min-w-0 flex-1">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0 pb-3">
            <CardTitle className="text-sm">
              {fields.length === 0
                ? "No fields yet"
                : `${fields.length} field${fields.length === 1 ? "" : "s"}`}
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowPreview(true)}
                disabled={fields.length === 0}
              >
                <Eye className="mr-1.5 h-4 w-4" />
                Preview
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange([])}
                disabled={disabled || fields.length === 0}
              >
                <Trash2 className="mr-1.5 h-4 w-4" />
                Clear
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {notice && <p className="text-destructive mb-3 text-sm">{notice}</p>}

            {fields.length === 0 ? (
              <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-center text-sm">
                <LayoutGrid className="h-6 w-6" />
                <p>Add a field from the left to start building this form.</p>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="flex flex-col gap-2">
                    {sections.map((section, i) =>
                      section.group === null ? (
                        section.fields.map((field) => (
                          <SortableFormElement
                            key={field.id}
                            element={field}
                            isSelected={field.id === editingId}
                            onSelect={setEditingId}
                            onEdit={setEditingId}
                            onRemove={removeField}
                          />
                        ))
                      ) : (
                        <SortableGroup
                          key={`${section.group}-${i}`}
                          groupId={section.group}
                          isDraggingField={draggingId !== null}
                        >
                          {section.fields.map((field) => (
                            <SortableFormElement
                              key={field.id}
                              element={field}
                              isSelected={field.id === editingId}
                              onSelect={setEditingId}
                              onEdit={setEditingId}
                              onRemove={removeField}
                            />
                          ))}
                        </SortableGroup>
                      ),
                    )}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {dragging && (
                    <div className="bg-card rounded-md border px-3 py-2 text-sm font-medium shadow-lg">
                      {dragging.label || dragging.key}
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-hidden">
          <DialogHeader>
            <DialogTitle>Field properties</DialogTitle>
            <DialogDescription>
              The label is what the agent reads. The key is what delivery
              templates read — change it only on purpose.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[65vh] pr-3">
            {editing && (
              <FormFieldEditor
                element={editing}
                onUpdate={updateField}
                onTypeChange={(type) => changeType(editing.id, type)}
              />
            )}
          </ScrollArea>
          {editing?.deletable && (
            <div className="flex justify-between border-t pt-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => duplicateField(editing.id)}
              >
                Duplicate
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeField(editing.id)}
              >
                Remove field
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>As the agent sees it</DialogTitle>
            <DialogDescription>
              Fill it in to try the form. Nothing here is recorded.
            </DialogDescription>
          </DialogHeader>
          <FormPreview elements={fields} formTitle={formTitle} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
